import type { NotificationOutcome } from '@sharedlib/notification-contract';
import type {
  NotificationCandidate,
  NotificationIdentity,
  NotificationOperation,
  NotificationPolicy,
  NotificationPresenter,
  NotificationRegistration,
  NotificationSession,
  NotificationView,
  PresenterActivation,
  Unsubscribe,
} from './ports';
import { noopNotificationPresenter } from './presenters';
import { isOpaqueId, isPresentation, snapshotCandidate } from './validation';

const retentionMs = 10 * 60 * 1000;
const maxDedupe = 2000;
const maxActions = 100;
const maxPending = 100;

export type DeliveryOutcome = NotificationOutcome | 'inactive' | 'expired' | 'denied' | 'unknown-category' | 'busy';

interface Attachment {
  readonly controller: AbortController;
  unsubscribe: Unsubscribe;
}

interface PendingOperation {
  readonly controller: AbortController;
  readonly scopeId: string;
  readonly registration: NotificationRegistration;
}

interface Action {
  readonly candidate: NotificationCandidate;
  readonly registration: NotificationRegistration;
  readonly expiresAt: number;
}

interface Run {
  readonly epoch: number;
  readonly identityGeneration: string;
  readonly scopes: Set<string>;
  readonly session: NotificationSession;
  readonly attachments: Map<NotificationRegistration, Attachment>;
  readonly operations: Set<PendingOperation>;
  readonly dedupe: Map<string, number>;
  readonly actions: Map<string, Action>;
  readonly pendingIds: Set<string>;
  unsubscribeActivation: Unsubscribe;
  view: NotificationView | null;
  contextRevision: number;
  expiryTimer?: ReturnType<typeof setTimeout>;
}

export interface NotificationCoordinatorOptions {
  readonly presenter?: NotificationPresenter;
  readonly policy?: NotificationPolicy;
  readonly now?: () => number;
}

/** Inert until explicitly started by an authenticated host. No domain or framework dependencies. */
export class NotificationCoordinator {
  private readonly registrations = new Map<string, NotificationRegistration>();
  private readonly presenter: NotificationPresenter;
  private readonly policy: NotificationPolicy;
  private readonly now: () => number;
  private epoch = 0;
  private current: Run | undefined;
  private transition: Promise<unknown> = Promise.resolve();

  public constructor(options: NotificationCoordinatorOptions = {}) {
    this.presenter = options.presenter ?? noopNotificationPresenter;
    this.policy = options.policy ?? { allows: () => false };
    this.now = options.now ?? Date.now;
  }

  public register(registration: NotificationRegistration): Unsubscribe {
    const { source, category } = registration.plugin;
    if (!isOpaqueId(source) || !isOpaqueId(category)) throw new Error('Invalid notification registration key.');
    const key = JSON.stringify([source, category]);
    if (this.registrations.has(key)) throw new Error('Conflicting notification registration.');
    this.registrations.set(key, registration);
    if (this.current) this.attach(this.current, registration);
    return () => {
      if (this.registrations.get(key) !== registration) return;
      this.registrations.delete(key);
      const run = this.current;
      if (!run) return;
      const attachment = run.attachments.get(registration);
      run.attachments.delete(registration);
      attachment?.controller.abort();
      try {
        attachment?.unsubscribe();
      } catch {
        /* One producer cannot prevent core cleanup. */
      }
      for (const operation of run.operations) if (operation.registration === registration) operation.controller.abort();
      const ids: string[] = [];
      for (const [id, action] of run.actions)
        if (action.registration === registration) {
          run.actions.delete(id);
          ids.push(id);
        }
      void run.session.dismiss(ids).catch(() => undefined);
    };
  }

  public start(identity: NotificationIdentity): Promise<boolean> {
    if (!isOpaqueId(identity.generation) || identity.scopeIds.length > 100 || !identity.scopeIds.every(isOpaqueId))
      return this.stop().then(() => false);
    const epoch = ++this.epoch;
    const identityGeneration = identity.generation;
    const prior = this.invalidate();
    const scopes = new Set(identity.scopeIds);
    const transition = this.transition
      .catch(() => undefined)
      .then(async () => {
        await prior?.session.close();
        if (epoch !== this.epoch || this.registrations.size === 0 || scopes.size === 0) return false;
        let session: NotificationSession | null;
        try {
          session = await this.presenter.open();
        } catch {
          return false;
        }
        if (!session) return false;
        if (epoch !== this.epoch) {
          await session.close();
          return false;
        }
        const run: Run = {
          epoch,
          identityGeneration,
          scopes,
          session,
          attachments: new Map(),
          operations: new Set(),
          dedupe: new Map(),
          actions: new Map(),
          pendingIds: new Set(),
          unsubscribeActivation: () => undefined,
          view: null,
          contextRevision: 0,
        };
        this.current = run;
        try {
          run.unsubscribeActivation = session.onActivated((event) => {
            void this.activate(run, event);
          });
          await session.updateContext(0);
          if (!this.isCurrent(run)) return false;
          for (const registration of this.registrations.values()) this.attach(run, registration);
          return true;
        } catch {
          if (this.current === run) this.invalidate();
          await session.close();
          return false;
        }
      });
    this.transition = transition;
    return transition;
  }

  public stop(): Promise<void> {
    this.epoch += 1;
    const prior = this.invalidate();
    const transition = this.transition
      .catch(() => undefined)
      .then(async () => {
        await prior?.session.close();
      });
    this.transition = transition;
    return transition;
  }

  public async updateView(view: NotificationView | null): Promise<void> {
    const run = this.current;
    if (!run) return;
    if (view && (!run.scopes.has(view.scopeId) || !isOpaqueId(view.key))) return;
    run.view = view ? { ...view } : null;
    run.contextRevision += 1;
    try {
      await run.session.updateContext(run.contextRevision);
    } catch {
      /* Main declines stale suppression hints. */
    }
  }

  public async removeScope(scopeId: string): Promise<void> {
    const run = this.current;
    if (!run || !run.scopes.delete(scopeId)) return;
    for (const operation of run.operations) if (operation.scopeId === scopeId) operation.controller.abort();
    for (const [id, action] of run.actions) if (action.candidate.scopeId === scopeId) run.actions.delete(id);
    if (run.view?.scopeId === scopeId) await this.updateView(null);
    try {
      await run.session.clearScope(scopeId);
    } catch {
      /* A disappeared document is already invalid. */
    }
  }

  /** Useful to explicit fixtures; real sources use the same bounded processing path. */
  public async deliver(input: unknown): Promise<DeliveryOutcome> {
    const run = this.current;
    if (!run) return 'inactive';
    const candidate = snapshotCandidate(input);
    if (!candidate) return 'invalid';
    const registration = this.registrations.get(JSON.stringify([candidate.source, candidate.category]));
    if (!registration) return 'unknown-category';
    return this.process(run, registration, candidate);
  }

  private attach(run: Run, registration: NotificationRegistration): void {
    if (!this.isCurrent(run) || run.attachments.has(registration)) return;
    const attachment: Attachment = { controller: new AbortController(), unsubscribe: () => undefined };
    run.attachments.set(registration, attachment);
    try {
      attachment.unsubscribe = registration.source.subscribe((input) => {
        if (!this.isCurrent(run) || attachment.controller.signal.aborted) return;
        const candidate = snapshotCandidate(input);
        if (
          !candidate ||
          candidate.source !== registration.plugin.source ||
          candidate.category !== registration.plugin.category
        )
          return;
        void this.process(run, registration, candidate);
      }, attachment.controller.signal);
      if (attachment.controller.signal.aborted) attachment.unsubscribe();
    } catch {
      attachment.controller.abort();
      run.attachments.delete(registration);
    }
  }

  private invalidate(): Run | undefined {
    const run = this.current;
    this.current = undefined;
    if (!run) return undefined;
    clearTimeout(run.expiryTimer);
    for (const operation of run.operations) operation.controller.abort();
    run.operations.clear();
    for (const attachment of run.attachments.values()) {
      attachment.controller.abort();
      try {
        attachment.unsubscribe();
      } catch {
        /* Continue cleaning other producers. */
      }
    }
    try {
      run.unsubscribeActivation();
    } catch {
      /* Still reset the main session. */
    }
    run.attachments.clear();
    run.actions.clear();
    run.dedupe.clear();
    run.pendingIds.clear();
    return run;
  }

  private isCurrent(run: Run): boolean {
    return this.current === run && run.epoch === this.epoch;
  }

  private eligible(
    run: Run,
    candidate: NotificationCandidate,
    registration: NotificationRegistration,
    controller: AbortController,
  ): boolean {
    return (
      this.isCurrent(run) &&
      !controller.signal.aborted &&
      run.scopes.has(candidate.scopeId) &&
      candidate.expiresAt > this.now() &&
      this.registrations.get(JSON.stringify([candidate.source, candidate.category])) === registration
    );
  }

  private prune(run: Run): void {
    const now = this.now();
    for (const [key, expiry] of run.dedupe) if (expiry <= now) run.dedupe.delete(key);
    const expired: string[] = [];
    for (const [id, action] of run.actions)
      if (action.expiresAt <= now) {
        run.actions.delete(id);
        expired.push(id);
      }
    if (expired.length) void run.session.dismiss(expired).catch(() => undefined);
    this.schedulePrune(run);
  }

  private schedulePrune(run: Run): void {
    clearTimeout(run.expiryTimer);
    run.expiryTimer = undefined;
    if (!this.isCurrent(run) || !run.actions.size) return;
    const expiry = Math.min(...[...run.actions.values()].map((action) => action.expiresAt));
    run.expiryTimer = setTimeout(
      () => {
        if (this.isCurrent(run)) this.prune(run);
      },
      Math.max(1, expiry - this.now()),
    );
  }

  private async process(
    run: Run,
    registration: NotificationRegistration,
    input: NotificationCandidate,
  ): Promise<DeliveryOutcome> {
    if (!this.isCurrent(run) || !run.scopes.has(input.scopeId)) return 'inactive';
    if (input.expiresAt <= this.now()) return 'expired';
    this.prune(run);
    const key = JSON.stringify([input.scopeId, input.dedupeId]);
    if (run.dedupe.has(key) || run.actions.has(input.id) || run.pendingIds.has(input.id)) return 'duplicate';
    if (run.operations.size >= maxPending) return 'busy';
    const candidate = { ...input };
    const operation: PendingOperation = { controller: new AbortController(), scopeId: candidate.scopeId, registration };
    const context: NotificationOperation = {
      signal: operation.controller.signal,
      identityGeneration: run.identityGeneration,
    };
    run.operations.add(operation);
    run.pendingIds.add(candidate.id);
    run.dedupe.set(key, this.now() + retentionMs);
    if (run.dedupe.size > maxDedupe) {
      const oldest = run.dedupe.keys().next();
      if (!oldest.done) run.dedupe.delete(oldest.value);
    }
    try {
      if (!registration.plugin.accepts(candidate)) return 'invalid';
      if (!(await this.policy.allows(candidate, context))) return 'denied';
      if (!this.eligible(run, candidate, registration, operation.controller)) return 'stale';
      const presentation = await registration.plugin.format(candidate, context);
      if (!this.eligible(run, candidate, registration, operation.controller)) return 'stale';
      if (!isPresentation(presentation)) return 'invalid';
      if (run.actions.size >= maxActions) {
        const oldest = run.actions.keys().next();
        if (!oldest.done) {
          run.actions.delete(oldest.value);
          void run.session.dismiss([oldest.value]).catch(() => undefined);
        }
      }
      if (!this.eligible(run, candidate, registration, operation.controller)) return 'stale';
      run.actions.set(candidate.id, {
        candidate,
        registration,
        expiresAt: Math.min(candidate.expiresAt, this.now() + retentionMs),
      });
      this.schedulePrune(run);
      const view = run.view;
      const response = await run.session.show({
        candidate,
        presentation,
        contextRevision: run.contextRevision,
        suppressIfAttentive: Boolean(
          view?.readable && view.scopeId === candidate.scopeId && view.key === presentation.viewKey,
        ),
      });
      if (!this.eligible(run, candidate, registration, operation.controller)) {
        await run.session.dismiss([candidate.id]);
        run.actions.delete(candidate.id);
        return 'stale';
      }
      if (response.status !== 'accepted') run.actions.delete(candidate.id);
      return response.status;
    } catch {
      run.actions.delete(candidate.id);
      return 'failed';
    } finally {
      run.operations.delete(operation);
      run.pendingIds.delete(candidate.id);
    }
  }

  private async activate(run: Run, event: PresenterActivation): Promise<void> {
    if (!this.isCurrent(run)) return;
    this.prune(run);
    const action = run.actions.get(event.id);
    run.actions.delete(event.id);
    const operation: PendingOperation | undefined = action
      ? { controller: new AbortController(), scopeId: action.candidate.scopeId, registration: action.registration }
      : undefined;
    if (operation) run.operations.add(operation);
    try {
      if (action && operation && this.eligible(run, action.candidate, action.registration, operation.controller)) {
        const context: NotificationOperation = {
          signal: operation.controller.signal,
          identityGeneration: run.identityGeneration,
        };
        if (await this.policy.allows(action.candidate, context)) {
          if (this.eligible(run, action.candidate, action.registration, operation.controller))
            await action.registration.plugin.activate(action.candidate, context);
        }
      }
    } catch {
      /* An unavailable/revoked target is not an app-wide navigation error. */
    } finally {
      if (operation) run.operations.delete(operation);
      if (this.isCurrent(run)) {
        try {
          await run.session.acknowledge(event.eventId);
        } catch {
          /* Replay remains bounded in main. */
        }
      }
    }
  }
}
